import React from 'react';
import { Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { TProspectInsurance } from 'types/common';
import { ELIGIBILITY_PRICE_CENTS } from 'types/insuranceEligibility';
import { IDLE_COPY, INSURANCE_GROUP_COPY, PICK_INSURANCE_COPY } from '../copy';
import { formatCents } from '../derive';
import { ManualIdleFoot } from './IdleCard';
import s from '../s.module.less';

type Props = {
    persona: 'managed' | 'manual';
    hasUpcomingBooking: boolean;
    // primary / secondary 两组，各自 default 在前
    primaries: TProspectInsurance[];
    secondaries: TProspectInsurance[];
    defaultId?: number;
    selectedId?: number;
    onSelect: (insuranceId: number) => void;
    onCheck: () => void;
};

// 从没查过：先单选一张再 check（10/05 起；10/22 起只有一张也出，次保险也能选）。
// 一律按 Primary / Secondary 分组——只有一张时组标签就是在告诉 provider 这张是哪种保险。
// 布局沿用原型 "Choose the plans to check"（顶条 + radio 卡 + 主按钮 + 价格提示），区别：
// 单选（一次 check 只查一张，价格也按一张算）；蓝不是琥珀（没有出错）；不折叠、顶条没有 Details——
// 这一步不选就走不下去，藏起来没有意义。两种 persona 都有；enrolled 不印价格、不弹 cost modal
const PickInsuranceCard: React.FC<Props> = ({
    persona,
    hasUpcomingBooking,
    primaries,
    secondaries,
    defaultId,
    selectedId,
    onSelect,
    onCheck,
}) => {
    const manual = persona === 'manual';
    const picked = selectedId ?? defaultId;
    return (
        <div className={s.idleWrap}>
            {/* enrolled：自动 check 的说明照旧放在最上方（有 booking 时系统会自己查 default），选择卡是「不等系统」的手动入口 */}
            {!manual && (
                <div className={s.idleHint}>
                    {hasUpcomingBooking ? IDLE_COPY.managedWithBooking : IDLE_COPY.managedNoBooking}
                </div>
            )}
            <div className={`${s.resCard} ${s.resCardOpen} ${s.pickInsurance}`}>
                <div className={`${s.resBar} ${s.tone_info}`}>
                    <span className={s.resLine}>{PICK_INSURANCE_COPY.barLine}</span>
                </div>
                <div className={s.pickBody}>
                    {[
                        { key: 'primary', label: INSURANCE_GROUP_COPY.primary, list: primaries },
                        { key: 'secondary', label: INSURANCE_GROUP_COPY.secondary, list: secondaries },
                    ]
                        .filter((g) => g.list.length > 0)
                        .map((g) => (
                            <React.Fragment key={g.key}>
                                <span className={s.insGroupLabel}>{g.label}</span>
                                <div className={s.radioCards} role="radiogroup">
                                    {g.list.map((ins) => {
                                        const on = ins.id === picked;
                                        const ids = PICK_INSURANCE_COPY.ids(ins.memberId, ins.groupId);
                                        return (
                                            <button
                                                key={ins.id}
                                                type="button"
                                                role="radio"
                                                aria-checked={on}
                                                className={`${s.radioCard} ${on ? s.radioCardOn : ''}`}
                                                onClick={() => ins.id != null && onSelect(ins.id)}
                                            >
                                                <span className={s.radioDot} />
                                                <span>
                                                    <span className={s.radioTitle}>
                                                        {[ins.carrier, ins.plan].filter(Boolean).join(' · ')}
                                                        {ins.id === defaultId && (
                                                            <span className={s.pickDefault}>
                                                                {PICK_INSURANCE_COPY.defaultTag}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {ids && <span className={s.radioDesc}>{ids}</span>}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </React.Fragment>
                        ))}
                    <div>
                        <Button type="primary" icon={<SearchOutlined />} onClick={onCheck} disabled={picked == null}>
                            {PICK_INSURANCE_COPY.cta(manual ? formatCents(ELIGIBILITY_PRICE_CENTS) : undefined)}
                        </Button>
                    </div>
                    {manual && <ManualIdleFoot />}
                    {/* 档案上没有 primary（只有次保险）时没有 default 可对照，不说这句 */}
                    {picked != null && defaultId != null && picked !== defaultId && (
                        <div className={s.infoLine}>{PICK_INSURANCE_COPY.notDefault}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PickInsuranceCard;
